import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import ReCAPTCHA from 'react-google-recaptcha';
import { 
  Star, 
  Send, 
  Building2, 
  Scissors,
  User,
  Clock,
  TrendingUp,
  Award
} from 'lucide-react';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';

const StarRating = ({ rating, onRatingChange, readonly = false, size = 'md' }) => {
  const [hoverRating, setHoverRating] = useState(0);
  const sizeClass = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-8 h-8' : 'w-6 h-6';
  
  return (
    <div className="flex gap-1" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => !readonly && onRatingChange(star)}
          onMouseEnter={() => !readonly && setHoverRating(star)}
          onMouseLeave={() => !readonly && setHoverRating(0)}
          disabled={readonly}
          className={`${readonly ? '' : 'cursor-pointer hover:scale-110'} transition-transform`}
          data-testid={`star-${star}`}
        >
          <Star
            className={`${sizeClass} ${
              (hoverRating || rating) >= star
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-400'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const RatingCard = ({ title, average, totalRatings, icon: Icon, breakdown }) => (
  <div className="bg-card border border-white/5 rounded-xl p-6" data-testid={`rating-card-${title}`}>
    <div className="flex items-center gap-3 mb-4">
      <div className="p-3 bg-primary/20 rounded-lg">
        <Icon className="w-6 h-6 text-primary" />
      </div>
      <div>
        <h3 className="font-bold text-lg">{title}</h3>
        <p className="text-sm text-muted-foreground">{totalRatings} تقييم</p>
      </div>
    </div>
    
    <div className="flex items-center gap-4 mb-4">
      <span className="text-4xl font-bold text-primary">{average}</span>
      <div>
        <StarRating rating={Math.round(average)} readonly size="md" />
        <p className="text-sm text-muted-foreground mt-1">من 5 نجوم</p>
      </div>
    </div>
    
    {breakdown && (
      <div className="space-y-2">
        {[5, 4, 3, 2, 1].map((star) => {
          const count = breakdown[star] || 0;
          const percentage = totalRatings > 0 ? (count / totalRatings) * 100 : 0;
          return (
            <div key={star} className="flex items-center gap-2 text-sm">
              <span className="w-4">{star}</span>
              <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
              <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className="w-8 text-muted-foreground">{count}</span>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

const Ratings = () => {
  const { user, isAdmin } = useAuth();
  const { language } = useLanguage();
  const recaptchaRef = useRef(null);
  
  const [salonRating, setSalonRating] = useState({ average: 0, total_ratings: 0, ratings_breakdown: {} });
  const [barbersRatings, setBarbersRatings] = useState([]);
  const [recentRatings, setRecentRatings] = useState({ salon_ratings: [], barber_ratings: [] });
  const [loading, setLoading] = useState(true);
  
  // Form states
  const [showSalonForm, setShowSalonForm] = useState(false);
  const [showBarberForm, setShowBarberForm] = useState(false);
  const [selectedBarber, setSelectedBarber] = useState(null);
  const [newSalonRating, setNewSalonRating] = useState(0);
  const [newBarberRating, setNewBarberRating] = useState(0);
  const [salonComment, setSalonComment] = useState('');
  const [barberComment, setBarberComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchRatings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRatings = async () => {
    try {
      const [salonRes, barbersRes] = await Promise.all([
        api.get('/ratings/salon/average'),
        api.get('/ratings/barbers/all')
      ]);
      
      setSalonRating(salonRes.data);
      setBarbersRatings(barbersRes.data);
      
      if (isAdmin()) {
        const recentRes = await api.get('/ratings/recent');
        setRecentRatings(recentRes.data);
      }
    } catch (error) {
      console.error('Error fetching ratings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSalonRatingSubmit = async () => {
    if (newSalonRating === 0) {
      toast.error('يرجى اختيار تقييم');
      return;
    }
    
    const token = recaptchaRef.current?.getValue();
    if (!token) {
      toast.error('يرجى إتمام التحقق reCAPTCHA');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/ratings/salon', {
        rating: newSalonRating,
        comment: salonComment,
        recaptcha_token: token
      });
      
      toast.success('شكراً لك! تم حفظ تقييمك');
      setShowSalonForm(false);
      setNewSalonRating(0);
      setSalonComment('');
      recaptchaRef.current?.reset();
      fetchRatings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إرسال التقييم');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBarberRatingSubmit = async () => {
    if (!selectedBarber || newBarberRating === 0) {
      toast.error('يرجى اختيار الحلاق والتقييم');
      return;
    }
    
    const token = recaptchaRef.current?.getValue();
    if (!token) {
      toast.error('يرجى إتمام التحقق reCAPTCHA');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/ratings/barber', {
        barber_id: selectedBarber.barber_id,
        rating: newBarberRating,
        comment: barberComment,
        recaptcha_token: token
      });
      
      toast.success(`شكراً لك! تم حفظ تقييمك للحلاق ${selectedBarber.barber_name}`);
      setShowBarberForm(false);
      setSelectedBarber(null);
      setNewBarberRating(0);
      setBarberComment('');
      recaptchaRef.current?.reset();
      fetchRatings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إرسال التقييم');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir={language === 'ar' ? 'rtl' : 'ltr'} data-testid="ratings-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">التقييمات</h1>
          <p className="text-muted-foreground">تقييمات العملاء للصالون والحلاقين</p>
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={() => setShowSalonForm(true)}
            className="bg-primary text-primary-foreground"
            data-testid="rate-salon-btn"
          >
            <Star className="w-4 h-4 me-2" />
            تقييم الصالون
          </Button>
          <Button 
            onClick={() => setShowBarberForm(true)}
            variant="outline"
            data-testid="rate-barber-btn"
          >
            <Scissors className="w-4 h-4 me-2" />
            تقييم حلاق
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card border border-white/5 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/20 rounded-lg">
              <TrendingUp className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">تقييم الصالون</p>
              <div className="flex items-center gap-2">
                <p className="text-2xl font-bold">{salonRating.average}</p>
                <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card border border-white/5 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-500/20 rounded-lg">
              <User className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">إجمالي التقييمات</p>
              <p className="text-2xl font-bold">{salonRating.total_ratings}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-white/5 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-500/20 rounded-lg">
              <Scissors className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">تقييم الحلاقين</p>
              <p className="text-2xl font-bold">{barbersRatings.filter(b => b.total_ratings > 0).length}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-white/5 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-yellow-500/20 rounded-lg">
              <Award className="w-6 h-6 text-yellow-500" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">أعلى تقييم للحلاق</p>
              <p className="text-2xl font-bold">
                {barbersRatings.length > 0 && barbersRatings[0].average > 0 
                  ? barbersRatings[0].average 
                  : '-'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Ratings Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Salon Rating */}
        <RatingCard
          title="تقييم الصالون"
          average={salonRating.average}
          totalRatings={salonRating.total_ratings}
          icon={Building2}
          breakdown={salonRating.ratings_breakdown}
        />

        {/* Barbers Ratings */}
        <div className="bg-card border border-white/5 rounded-xl p-6" data-testid="barbers-ratings-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-primary/20 rounded-lg">
              <Scissors className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-bold text-lg">تقييمات الحلاقين</h3>
              <p className="text-sm text-muted-foreground">{barbersRatings.length} حلاق</p>
            </div>
          </div>
          
          <div className="space-y-4">
            {barbersRatings.map((barber, index) => (
              <div 
                key={barber.barber_id} 
                className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg"
                data-testid={`barber-rating-${barber.barber_id}`}
              >
                <div className="flex items-center gap-3">
                  {index === 0 && barber.average > 0 && (
                    <Award className="w-5 h-5 text-yellow-400" />
                  )}
                  <div>
                    <p className="font-medium">{barber.barber_name}</p>
                    <p className="text-sm text-muted-foreground">{barber.total_ratings} تقييم</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StarRating rating={Math.round(barber.average)} readonly size="sm" />
                  <span className="font-bold text-primary">{barber.average}</span>
                </div>
              </div>
            ))}
            
            {barbersRatings.length === 0 && (
              <p className="text-center text-muted-foreground py-4">لا توجد تقييمات بعد</p>
            )}
          </div>
        </div>
      </div>

      {/* Recent Ratings (Admin Only) */}
      {isAdmin() && (
        <div className="bg-card border border-white/5 rounded-xl p-6" data-testid="recent-ratings">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-primary/20 rounded-lg">
              <Clock className="w-6 h-6 text-primary" />
            </div>
            <h3 className="font-bold text-lg">آخر التقييمات</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Recent Salon Ratings */}
            <div>
              <h4 className="font-medium mb-3">تقييمات الصالون</h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {recentRatings.salon_ratings.map((r) => (
                  <div key={r.id} className="p-3 bg-secondary/30 rounded-lg text-sm">
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-medium">{r.user_name}</span>
                      <StarRating rating={r.rating} readonly size="sm" />
                    </div>
                    {r.comment && <p className="text-muted-foreground">{r.comment}</p>}
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(r.created_at).toLocaleDateString('ar-AE')}
                    </p>
                  </div>
                ))}
                {recentRatings.salon_ratings.length === 0 && (
                  <p className="text-muted-foreground text-center py-2">لا توجد تقييمات</p>
                )}
              </div>
            </div>
            
            {/* Recent Barber Ratings */}
            <div>
              <h4 className="font-medium mb-3">تقييمات الحلاقين</h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {recentRatings.barber_ratings.map((r) => (
                  <div key={r.id} className="p-3 bg-secondary/30 rounded-lg text-sm">
                    <div className="flex justify-between items-center mb-1">
                      <div>
                        <span className="font-medium">{r.user_name}</span>
                        <span className="text-muted-foreground mx-2">→</span>
                        <span className="text-primary">{r.barber_name}</span>
                      </div>
                      <StarRating rating={r.rating} readonly size="sm" />
                    </div>
                    {r.comment && <p className="text-muted-foreground">{r.comment}</p>}
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(r.created_at).toLocaleDateString('ar-AE')}
                    </p>
                  </div>
                ))}
                {recentRatings.barber_ratings.length === 0 && (
                  <p className="text-muted-foreground text-center py-2">لا توجد تقييمات</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Salon Rating Modal */}
      {showSalonForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="salon-rating-modal">
          <div className="bg-card border border-white/10 rounded-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">تقييم الصالون</h2>
            
            <div className="space-y-4">
              <div className="text-center py-4">
                <p className="mb-3 text-muted-foreground">ما هو تقييمك للصالون؟</p>
                <div className="flex justify-center">
                  <StarRating 
                    rating={newSalonRating} 
                    onRatingChange={setNewSalonRating}
                    size="lg"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">تعليق (اختياري)</label>
                <Textarea
                  value={salonComment}
                  onChange={(e) => setSalonComment(e.target.value)}
                  placeholder="شاركنا رأيك..."
                  className="bg-secondary/50"
                  rows={3}
                />
              </div>
              
              <div className="flex justify-center">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                  theme="dark"
                />
              </div>
              
              <div className="flex gap-3">
                <Button 
                  onClick={handleSalonRatingSubmit}
                  disabled={submitting || newSalonRating === 0}
                  className="flex-1 bg-primary text-primary-foreground"
                >
                  <Send className="w-4 h-4 me-2" />
                  {submitting ? 'جاري الإرسال...' : 'إرسال التقييم'}
                </Button>
                <Button 
                  onClick={() => {
                    setShowSalonForm(false);
                    setNewSalonRating(0);
                    setSalonComment('');
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  إلغاء
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Barber Rating Modal */}
      {showBarberForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="barber-rating-modal">
          <div className="bg-card border border-white/10 rounded-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">تقييم حلاق</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">اختر الحلاق</label>
                <div className="grid grid-cols-2 gap-2">
                  {barbersRatings.map((barber) => (
                    <button
                      key={barber.barber_id}
                      onClick={() => setSelectedBarber(barber)}
                      className={`p-3 rounded-lg border transition-all ${
                        selectedBarber?.barber_id === barber.barber_id
                          ? 'border-primary bg-primary/20'
                          : 'border-white/10 hover:border-white/30'
                      }`}
                    >
                      <p className="font-medium">{barber.barber_name}</p>
                      <div className="flex items-center justify-center gap-1 mt-1">
                        <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                        <span className="text-sm">{barber.average}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              
              {selectedBarber && (
                <div className="text-center py-4 border-t border-white/10">
                  <p className="mb-3 text-muted-foreground">تقييمك لـ {selectedBarber.barber_name}</p>
                  <div className="flex justify-center">
                    <StarRating 
                      rating={newBarberRating} 
                      onRatingChange={setNewBarberRating}
                      size="lg"
                    />
                  </div>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium mb-2">تعليق (اختياري)</label>
                <Textarea
                  value={barberComment}
                  onChange={(e) => setBarberComment(e.target.value)}
                  placeholder="شاركنا رأيك..."
                  className="bg-secondary/50"
                  rows={3}
                />
              </div>
              
              <div className="flex justify-center">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                  theme="dark"
                />
              </div>
              
              <div className="flex gap-3">
                <Button 
                  onClick={handleBarberRatingSubmit}
                  disabled={submitting || !selectedBarber || newBarberRating === 0}
                  className="flex-1 bg-primary text-primary-foreground"
                >
                  <Send className="w-4 h-4 me-2" />
                  {submitting ? 'جاري الإرسال...' : 'إرسال التقييم'}
                </Button>
                <Button 
                  onClick={() => {
                    setShowBarberForm(false);
                    setSelectedBarber(null);
                    setNewBarberRating(0);
                    setBarberComment('');
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  إلغاء
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Ratings;
